import React from "react";
import styled, { keyframes, css } from "styled-components";
import { zIndex } from "../styles/designSystem";

/**
 * CRT screen overlay.
 *
 * Three stacked, pointer-transparent layers sit above the entire app and sell the
 * "this is a cathode-ray terminal" read:
 *   1. Scanlines  — 1px dark rules every 3px, plus a bright band that sweeps down.
 *   2. Vignette   — corner falloff + phosphor tint, so the screen looks curved.
 *   3. Flicker    — a barely-there opacity wobble on the whole screen.
 *
 * All of it is disabled under prefers-reduced-motion, and the sweep is dropped on
 * small screens where a full-viewport animated gradient is not worth the battery.
 */

const sweep = keyframes`
    0%   { transform: translateY(-20vh); }
    100% { transform: translateY(120vh); }
`;

const flicker = keyframes`
    0%, 100% { opacity: 0.985; }
    12%      { opacity: 1; }
    23%      { opacity: 0.97; }
    41%      { opacity: 1; }
    58%      { opacity: 0.978; }
    77%      { opacity: 1; }
    89%      { opacity: 0.965; }
`;

const Layer = styled.div`
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: ${zIndex.crt};
`;

const Scanlines = styled(Layer)`
    background-image: repeating-linear-gradient(
        to bottom,
        ${({ theme }) => theme.scanline} 0px,
        ${({ theme }) => theme.scanline} 1px,
        transparent 1px,
        transparent 3px
    );
    opacity: 0.5;
    mix-blend-mode: multiply;
`;

const Sweep = styled(Layer)`
    overflow: hidden;

    &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        height: 22vh;
        background: linear-gradient(
            to bottom,
            transparent,
            ${({ theme }) => theme.glowSoft},
            transparent
        );
        ${css`animation: ${sweep} 7s linear infinite;`}
    }

    @media (max-width: 768px) {
        display: none;
    }

    @media (prefers-reduced-motion: reduce) {
        display: none;
    }
`;

const Vignette = styled(Layer)`
    background:
        radial-gradient(
            ellipse at center,
            transparent 45%,
            rgba(0, 0, 0, 0.35) 85%,
            rgba(0, 0, 0, 0.7) 100%
        ),
        linear-gradient(${({ theme }) => theme.screenTint}, ${({ theme }) => theme.screenTint});
    ${css`animation: ${flicker} 4.5s infinite steps(1, end);`}

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const CRTOverlay = () => (
    <>
        <Scanlines aria-hidden="true" />
        <Sweep aria-hidden="true" />
        <Vignette aria-hidden="true" />
    </>
);

export default CRTOverlay;
